import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ContentFormat } from '@/api/api';

interface TiptapEditorProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

// Helper function to convert HTML to plain text
function htmlToText(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html.replace('</p>', '</p>\n').replace('</div>', '</div>\n');
  return div.textContent || div.innerText || '';
}

// Helper function to convert plain text to HTML
function textToHtml(text: string): string {
  return '<p>' + text.split('\n').join('</p><p>') + '</p>';
}

export function TiptapEditor({
  value = '',
  onChange,
  placeholder = '',
  disabled = false,
  icon,
}: TiptapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure based on HTML analysis - only allow elements found in content
        // NOTE: 'document' must NOT be disabled - it's required for schema
        heading: { levels: [2] }, // Only h2 headings found
        bulletList: false, // No bullet lists found in content
        orderedList: {}, // ol/li elements found
        listItem: {},
        paragraph: {},
        bold: {},
        italic: {},
        hardBreak: {}, // br elements found
        // Disable unused features
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank', // All links in content use target="_blank"
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editable: !disabled,
    immediatelyRender: false,
  });

  useEffect(() => {
    if (editor && editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  if (!editor) {
    return (
      <div className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
        Loading editor...
      </div>
    );
  }

  return (
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-3 z-10 text-sm opacity-60">
          {icon}
        </div>
      )}
      <div className={cn('border border-input rounded-md', icon && 'pl-8')}>
        <div className="border-b border-border px-3 py-2 bg-muted/50">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
              disabled={disabled}
              className={cn(
                'px-2 py-1 text-xs rounded hover:bg-background',
                editor.isActive('heading', { level: 2 }) &&
                  'bg-background font-bold'
              )}
            >
              H2
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={disabled}
              className={cn(
                'px-2 py-1 text-xs rounded hover:bg-background',
                editor.isActive('bold') && 'bg-background font-bold'
              )}
            >
              B
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={disabled}
              className={cn(
                'px-2 py-1 text-xs rounded hover:bg-background italic',
                editor.isActive('italic') && 'bg-background'
              )}
            >
              I
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              disabled={disabled}
              className={cn(
                'px-2 py-1 text-xs rounded hover:bg-background',
                editor.isActive('orderedList') && 'bg-background'
              )}
            >
              1.
            </button>
            <button
              type="button"
              onClick={() => {
                const url = window.prompt('Enter URL:');
                if (url) {
                  editor
                    .chain()
                    .focus()
                    .setLink({ href: url, target: '_blank' })
                    .run();
                }
              }}
              disabled={disabled}
              className={cn(
                'px-2 py-1 text-xs rounded hover:bg-background',
                editor.isActive('link') && 'bg-background'
              )}
            >
              🔗
            </button>
          </div>
        </div>
        <div className="px-3 py-2 min-h-[80px]">
          <EditorContent
            editor={editor}
            className={cn(
              'prose prose-sm max-w-none bg-white rounded-[6px] p-2',
              '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[60px]',
              disabled && 'opacity-50'
            )}
          />
        </div>
      </div>
    </div>
  );
}
